import React from 'react';
import { Calendar, CheckCircle, Circle, Clock, AlertTriangle, Target, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { startupTaskGenerator } from '@/services/startupTaskGenerator';

interface Task {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  priority?: 'high' | 'medium' | 'low';
  due_date?: string;
  category?: string;
  created_at: string;
}

interface AllTasksViewProps {
  tasks: Task[];
  onTaskToggle: (taskId: string) => void;
  onClose?: () => void;
}

// Helper text for common tasks - now using startup task generator for better tips
const getTaskHelp = (title: string): string => {
  // First check if this is a startup methodology task
  const startupTip = startupTaskGenerator.getTaskTip(title);
  
  if (startupTip !== 'Break this down into smaller actionable steps for better progress tracking') {
    return startupTip;
  }
  
  // Fallback to basic helpers for other tasks
  const helpers: Record<string, string> = {
    'domain': 'Use Hostinger tool from Useful Tools tab for domain registration',
    'website': 'Check out web development tools in Useful Tools section',
    'landing': 'Use no-code tools like Framer or Webflow from Useful Tools',
    'validate': 'Complete your Idea Assessment in the validation section',
    'customer': 'Use the Community Pods to connect with potential users',
    'payment': 'Set up Stripe or PayPal for payment processing',
    'analytics': 'Add Google Analytics or use the ROI Tracker',
    'social': 'Create social media presence for marketing',
    'email': 'Set up email marketing with tools like Mailchimp',
    'logo': 'Use design tools or hire from the Community Pods',
  };
  
  const lowerTitle = title.toLowerCase();
  for (const [key, help] of Object.entries(helpers)) {
    if (lowerTitle.includes(key)) {
      return help;
    }
  }
  
  return 'Break this down into smaller actionable steps for better progress tracking';
};

const getPriorityColor = (priority?: string) => {
  switch (priority) {
    case 'high': return 'text-startup-red';
    case 'medium': return 'text-startup-gold';
    case 'low': return 'text-startup-gray';
    default: return 'text-startup-gray';
  }
};

const getPriorityIcon = (priority?: string) => {
  switch (priority) {
    case 'high': return <AlertTriangle className="w-4 h-4" />;
    case 'medium': return <Clock className="w-4 h-4" />;
    case 'low': return <Circle className="w-4 h-4" />;
    default: return <Circle className="w-4 h-4" />;
  }
};

export const AllTasksView: React.FC<AllTasksViewProps> = ({
  tasks,
  onTaskToggle,
  onClose
}) => {
  // Group tasks by due date
  const groupTasksByDate = () => {
    const groups: Record<string, Task[]> = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Process all tasks with due dates
    tasks.forEach(task => {
      if (!task.due_date) return;
      
      const taskDate = new Date(task.due_date);
      taskDate.setHours(0, 0, 0, 0);
      
        // Only show future and today's tasks, or past incomplete tasks
        // For overdue: only show tasks that are strictly before today AND not completed
        if (taskDate < today && task.completed) return;
      
      const dateKey = taskDate.toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(task);
    });
    
    // Process tasks without due dates - assign them to their creation date
    tasks.forEach(task => {
      if (!task.due_date && task.created_at) {
        const createdDate = new Date(task.created_at);
        createdDate.setHours(0, 0, 0, 0);
        
        // Only show if it's today or in the future, or if it's a past incomplete task
        if (createdDate < today && task.completed) return;
        
        const dateKey = createdDate.toDateString();
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(task);
      }
    });
    
    // Sort dates (today first, then future dates, then past dates)
    const sortedDates = Object.keys(groups).sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      if (dateA.toDateString() === today.toDateString()) return -1;
      if (dateB.toDateString() === today.toDateString()) return 1;
      return dateA.getTime() - dateB.getTime();
    });
    
    return sortedDates.map(date => ({
      date,
      tasks: groups[date].sort((a, b) => {
        // Completed tasks at the bottom
        if (a.completed !== b.completed) {
          return a.completed ? 1 : -1;
        }
        // Then by priority
        const priorityOrder: { [key: string]: number } = { high: 0, medium: 1, low: 2 };
        return (priorityOrder[a.priority || 'medium'] || 1) - (priorityOrder[b.priority || 'medium'] || 1);
      })
    }));
  };

  const groupedTasks = groupTasksByDate();
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-startup-navy flex items-center">
              <Target className="w-8 h-8 mr-3 text-startup-teal" />
              All Tasks ({tasks.length})
            </h1>
            <p className="text-startup-gray mt-1">
              Complete view of your tasks organized by day
            </p>
          </div>
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Back to Dashboard
            </Button>
          )}
        </div>

        {/* Task Summary */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-startup-teal">
              {tasks.filter(t => !t.completed).length}
            </div>
            <div className="text-sm text-startup-gray">Pending Tasks</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-startup-gold">
              {tasks.filter(t => t.completed).length}
            </div>
            <div className="text-sm text-startup-gray">Completed Tasks</div>
          </Card>
          <Card className="p-4 text-center">
            <div className="text-2xl font-bold text-startup-navy">
              {Math.round((tasks.filter(t => t.completed).length / tasks.length) * 100)}%
            </div>
            <div className="text-sm text-startup-gray">Completion Rate</div>
          </Card>
        </div>

        {/* Tasks by Date */}
        <ScrollArea className="h-[600px]">
          <div className="space-y-6">
            {groupedTasks.length === 0 ? (
              <div className="text-center py-12">
                <Target className="w-16 h-16 text-startup-gray/50 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-startup-navy mb-2">No Tasks Yet</h3>
                <p className="text-startup-gray">Start by adding your first task from the Dashboard!</p>
              </div>
            ) : (
              groupedTasks.map(({ date, tasks: dayTasks }) => (
                <div key={date} className="space-y-4">
                  {/* Date Header */}
                  <div className="flex items-center gap-3 pb-2 border-b border-startup-gray/20">
                    <Calendar className="w-5 h-5 text-startup-teal" />
                    <h2 className="text-xl font-semibold text-startup-navy">
                      {formatDate(date)}
                    </h2>
                    <Badge variant="outline" className="ml-auto">
                      {dayTasks.length} tasks
                    </Badge>
                  </div>

                  {/* Tasks for this date */}
                  <div className="space-y-3 pl-8">
                     {dayTasks.map((task, index) => (
                      <Card 
                        key={`${task.id}-${date}`}
                        className={`p-4 transition-all duration-300 hover:shadow-md animate-fade-in ${
                          task.completed ? 'bg-startup-slate/50' : 'bg-white'
                        }`}
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <div className="flex items-start gap-4">
                          {/* Completion Toggle */}
                          <button
                            onClick={() => onTaskToggle(task.id)}
                            className={`mt-1 w-6 h-6 border-2 rounded-full flex items-center justify-center transition-all duration-300 ${
                              task.completed 
                                ? 'bg-startup-teal border-startup-teal text-white' 
                                : 'border-startup-gray hover:border-startup-teal'
                            }`}
                          >
                            {task.completed && <CheckCircle className="w-4 h-4" />}
                          </button>

                          {/* Task Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className={`font-medium ${
                                task.completed ? 'text-startup-gray line-through' : 'text-startup-navy'
                              }`}>
                                {task.title}
                              </h4>
                              <div className={`flex items-center gap-1 ${getPriorityColor(task.priority)}`}>
                                {getPriorityIcon(task.priority)}
                              </div>
                            </div>
                            
                            {task.description && (
                              <p className="text-startup-gray text-sm mb-2">
                                {task.description}
                              </p>
                            )}

                            {/* Task Help */}
                            <div className="flex items-start gap-2 p-3 bg-startup-slate/30 rounded-lg text-sm">
                              <Lightbulb className="w-4 h-4 text-startup-gold mt-0.5 flex-shrink-0" />
                              <p className="text-startup-gray">
                                <strong>Tip:</strong> {getTaskHelp(task.title)}
                              </p>
                            </div>

                            {/* Metadata */}
                            <div className="flex items-center gap-4 mt-2 text-xs text-startup-gray">
                              {task.category && (
                                <Badge variant="outline" className="text-xs">
                                  {task.category}
                                </Badge>
                              )}
                              {task.due_date && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Due: {new Date(task.due_date).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default AllTasksView;